import { useState, useEffect } from 'react';
import { FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import BackToTop from '@/components/BackToTop';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TermsOfServicePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/legal/terms-of-service`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <Helmet>
        <title>Terms of Service | UI DESIGNS</title>
        <meta name="description" content="Terms of Service for UI DESIGNS web design and maintenance services." />
      </Helmet>
      <Navigation />

      {/* Header */}
      <div className="relative bg-[#0A0A0A] pt-32 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-[#00E5FF]/5 rounded-full blur-[120px]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#00E5FF] transition-colors mb-8" data-testid="terms-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#00E5FF]" />
            </div>
            <div>
              <h1 data-testid="terms-page-title" className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
                {data?.title || 'Terms of Service'}
              </h1>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">Last updated: {data?.last_updated || 'April 2026'}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <article
            data-testid="terms-content"
            className="prose prose-slate dark:prose-invert max-w-none
              prose-headings:font-['Outfit'] prose-headings:tracking-tight
              prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-3 prose-h2:border-b prose-h2:border-slate-200 prose-h2:dark:border-white/10
              prose-p:text-base prose-p:leading-relaxed prose-p:text-slate-600 prose-p:dark:text-slate-400
              prose-li:text-slate-600 prose-li:dark:text-slate-400
              prose-strong:text-slate-800 prose-strong:dark:text-white
              prose-a:text-[#00B4D8] prose-a:no-underline hover:prose-a:underline
              prose-ul:my-4 prose-li:my-1"
            dangerouslySetInnerHTML={{ __html: data?.content || '' }}
          />
        )}

        {/* Contact callout */}
        <div className="mt-16 p-6 sm:p-8 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            If you have any questions about these terms, please contact us at{' '}
            <a href="mailto:info@uidesigns.co.uk" className="text-[#00B4D8] hover:underline">info@uidesigns.co.uk</a>.
          </p>
        </div>
      </div>

      <Footer />
      <CookieConsent />
      <BackToTop />
    </div>
  );
}
